import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import { Provider } from "react-redux";
import store from "../src/contents/redux/store/store";
import "react-toastify/dist/ReactToastify.css";
import { refreshAccessToken } from "./contents/redux/actions/authActions";
import Joyride, { STATUS } from "react-joyride";
import { stopTour, nextStep } from "./contents/redux/actions/userGuideActions";
import { useJoyrideStepPreparation } from "./components/utilities/joyrideProcess";
import styles from "./components/utilities/cssmodules/App.module.css";

import RequestAccess from "./components/RequestAccess.js";
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import CreatePassword from "./components/CreatePassword";
import ChangePassword from "./components/changepassword/ChangePassword";
import Dashboard from "./components/dashboard/Dashboard";
import Sidebar from "./components/sidebar/Sidebar";
import RoleManagement from "./components/rolemanagement/RoleManagement";
import UserManagement from "./components/usermanagement/UserManagement";
import MasterList from "./components/masterlist/MasterList.js";
import AccessRequests from "./components/accessrequests/AccessRequests.js";
import MembershipApplication from "./components/membershipapplication/MembershipApplication.js";
import Membership from "./components/membership/Membership.js";
import MembershipDirectory from "./components/membershipdirectory/MembershipDirectory.js";
import ViewMembershipRequestContent from "./components/membership/ViewMembershipRequestContent.js";
import MembershipApprovalsContent from "./components/membershipapprovals/MembershipApprovalsContent.js";
import MembershipTiers from "./components/membershiptiers/MembershipTiers.js";
import ViewAccessRequestContent from "./components/accessrequests/ViewAccessRequestContent.js";
import Payments from "./components/payments/Payments.js";
import MyPayments from "./components/mypayments/MyPaymentsContent.js";
import PaymentReceipt from "./components/mypayments/PaymentReceipt.js";
import Support from "./components/help&support/Support.js";
import Notifications from "./components/notifications/Notifications";
import ConfigureWorkflow from "./components/workflowmanagement/ConfigureWorkflow";
import ViewRoleContent from "./components/rolemanagement/ViewRoleContent";
import FeedbackSharing from "./components/feedback/FeedbackSharing.js";
import ConfigureRolePermissions from "./components/rolemanagement/ConfigureRolePermissions";
import WorkflowManagement from "./components/workflowmanagement/WorkflowManagement";
import ViewWorkflowContent from "./components/workflowmanagement/ViewWorkflowContent";
import ConfigureWorkflowPermissions from "./components/workflowmanagement/ConfigureWorkflowPermissions";
import Topbar from "./components/topbar/Topbar";
import Profile from "./components/profile/Profile";
import Settings from "./components/settings/Settings";
import BugReporting from "./components/feedback/BugReporting.js";
import NetworkStatus from "./components/utilities/NetworkStatus";
import NotFound from "./components/NotFound";



import MyRequests from "./components/requestmanagement/myrequests/MyRequests";
import AllRequests from "./components/requestmanagement/allrequests/AllRequests";
import PendingRequests from "./components/requestmanagement/pendingrequests/PendingRequests";
import PendingReceiptUploads from "./components/requestmanagement/pendingreceiptuploads/PendingReceiptUploads";
import PendingReceiptApprovals from "./components/requestmanagement/pendingreceiptapprovals/PendingReceiptApprovals";
import PaymentRequest from "./components/requestmanagement/requesttype/payment/PaymentRequest";
import ExecutiveRequest from "./components/requestmanagement/requesttype/executive/ExecutiveRequest";
import ViewExecutiveRequest from "./components/requestmanagement/requesttype/executive/ViewExecutiveRequest";
import EditExecutiveRequest from "./components/requestmanagement/requesttype/executive/EditExecutiveRequest";
import ViewPaymentVoucher from "./components/requestmanagement/requesttype/payment/ViewPaymentVoucher";
import EditPaymentRequest from "./components/requestmanagement/requesttype/payment/EditPaymentRequest";
import SalaryAdvance from "./components/requestmanagement/requesttype/salaryadvance/SalaryAdvance";
import ViewSalaryAdvance from "./components/requestmanagement/requesttype/salaryadvance/ViewSalaryAdvance";
import EditSalaryAdvance from "./components/requestmanagement/requesttype/salaryadvance/EditSalaryAdvance";
import Loan from "./components/requestmanagement/requesttype/loan/Loan";
import ViewLoan from "./components/requestmanagement/requesttype/loan/ViewLoan";
import EditLoan from "./components/requestmanagement/requesttype/loan/EditLoan";
import PurposeManagement from "./components/settings/purposemanagement/PurposeManagement";
import DailyRateManagement from "./components/settings/dailyratemanagement/DailyRateManagement";
import ProjectManagement from "./components/settings/projectmanagement/ProjectManagement.js";
import DepartmentManagement from "./components/settings/departmentmanagement/DepartmentManagement";
import TrackRequests from "./components/requestmanagement/trackrequests/TrackRequests";
import InProgress from "./components/requestmanagement/trackrequests/InProgress";
import CompletedRequests from "./components/requestmanagement/trackrequests/CompletedRequests";
import QueriedRequests from "./components/requestmanagement/trackrequests/QueriedRequests";
import RejectedRequests from "./components/requestmanagement/trackrequests/RejectedRequests";

function AppContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const {
    currentSteps,
    isRunning,
    currentStep,
    isStepReady,
    lastStepPrepared,
  } = useSelector((state) => state.userGuide);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showNetworkRestoredMessage, setShowNetworkRestoredMessage] =
    useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleStatusChange = (status) => {
    setIsOnline(status);
    if (status) {
      setShowNetworkRestoredMessage(true);
      setTimeout(() => setShowNetworkRestoredMessage(false), 4000);
    }
  };

  const toggleSidebar = () => {
    const startTime = performance.now();
    setSidebarOpen((prev) => {
      const newState = !prev;
      const endTime = performance.now();
      console.log(
        `Toggling sidebar, new state: ${newState}, time: ${(endTime - startTime).toFixed(2)}ms`,
      );
      return newState;
    });
  };

  useEffect(() => {
    const handleOnline = () => handleStatusChange(true);
    const handleOffline = () => handleStatusChange(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && sidebarOpen) {
        console.log("Escape key pressed, closing sidebar");
        setSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sidebarOpen]);

  useEffect(() => {
    if (isLoggedIn && accessToken) {
      const intervalId = setInterval(
        () => {
          dispatch(refreshAccessToken());
        },
        55 * 60 * 1000,
      );
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, accessToken, dispatch]);

  useJoyrideStepPreparation(navigate, location);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;
    if (action === "next" && type === "step:after") {
      dispatch(nextStep());
    }
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      dispatch(stopTour());
    }
  };

  return (
    <>
      <NetworkStatus
        isOnline={isOnline}
        showNetworkRestoredMessage={showNetworkRestoredMessage}
      />
      <ToastContainer autoClose={4000} />
      {!isLoggedIn ? (
        <Routes>
          <Route path="*" element={<Login />} />
          <Route path="/requestaccess" element={<RequestAccess />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/createpassword" element={<CreatePassword />} />
        </Routes>
      ) : (
        <div className={styles["app-wrapper"]}>
          <div className={`${styles["sidebar-wrapper"]} ${sidebarOpen ? styles.active : ""}`}>
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          </div>
          <div
            className={`${styles["sidebar-overlay"]} ${sidebarOpen ? styles.active : ""}`}
            onClick={() => {
              toggleSidebar();
            }}
          ></div>
          <div className={styles["content-area-wrapper"]}>
            <div className={styles["top-bar"]}>
              <Topbar toggleSidebar={toggleSidebar} />
            </div>
            <div className={styles["main-content-area"]}>
              <Routes>
                <Route index element={<Navigate to="/dashboard" />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/rolemanagement" element={<RoleManagement />} />
                <Route path="/usermanagement" element={<UserManagement />} />
                <Route path="/masterlist" element={<MasterList />} />
                <Route path="/accessrequests" element={<AccessRequests />} />
                <Route path="/viewaccessrequest" element={<ViewAccessRequestContent />} />
                <Route path="/membershipapplication" element={<MembershipApplication />} />
                <Route path="/membershipdirectory" element={<MembershipDirectory />} />
                <Route path="/membershiprequests" element={<Membership />} />
                <Route path="/membershiptiersmanagement" element={<MembershipTiers />} />
                <Route path="/viewmembershiprequest" element={<ViewMembershipRequestContent />} />
                <Route path="/membershipapprovals" element={<MembershipApprovalsContent />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/mypayments" element={<MyPayments />} />
                <Route path="/paymentreceipt" element={<PaymentReceipt />} />
                <Route path="/rolemanagement/viewrole" element={<ViewRoleContent />}/>
                <Route path="/rolemanagement/configurepermissions" element={<ConfigureRolePermissions />}/>
                <Route path="/workflowmanagement" element={<WorkflowManagement />}/>
                <Route path="/workflowmanagement/viewworkflow" element={<ViewWorkflowContent />}/>
                <Route path="/workflowmanagement/configureworkflow" element={<ConfigureWorkflow />}/>
                <Route path="/workflowmanagement/configureworkflowpermissions" element={<ConfigureWorkflowPermissions />}/>
                <Route path="/settings" element={<Settings />} />
                <Route path="/help&support" element={<Support />} />
                <Route path="/bugreporting" element={<BugReporting />} />
                <Route path="/feedback" element={<FeedbackSharing />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/changepassword" element={<ChangePassword />} />
                


                <Route path="/myrequests" element={<MyRequests />} />
                <Route path="/trackrequests" element={<TrackRequests />} />
                <Route path="/inprogress" element={<InProgress />} />
                <Route path="/completedrequests" element={<CompletedRequests />} />
                <Route path="/queriedrequests" element={<QueriedRequests />} />
                <Route path="/rejectedrequests" element={<RejectedRequests />} />
                <Route path="/allrequests" element={<AllRequests />} />
                <Route path="/pendingrequests" element={<PendingRequests />} />
                <Route path="/pendingreceiptuploads" element={<PendingReceiptUploads />} />
                <Route path="/pendingreceiptapprovals" element={<PendingReceiptApprovals />}/>
                <Route path="/requestmanagement/requesttype/payment" element={<PaymentRequest />}/>
                <Route path="/requestmanagement/requesttype/executive" element={<ExecutiveRequest />} />
                <Route path="/edit-payment-voucher" element={<EditPaymentRequest />} />
                <Route path="/edit-salary-advance" element={<EditSalaryAdvance />}/>
                <Route path="/edit-loan-request" element={<EditLoan />} />
                <Route path="/requestmanagement/requesttype/salaryadvance" element={<SalaryAdvance />} />
                <Route path="/requestmanagement/requesttype/loan" element={<Loan />}/>
                <Route path="/loan-request" element={<ViewLoan />} />
                <Route path="/salary-advance-request" element={<ViewSalaryAdvance />}/>
                <Route path="/payment-voucher-request" element={<ViewPaymentVoucher />}/>
                <Route path="/executive-request" element={<ViewExecutiveRequest />}/>
                <Route path="/edit-executive-request" element={<EditExecutiveRequest />}/>
                <Route path="/settings/project/Projectmanagement" element={<ProjectManagement />}/>
                <Route path="/settings/purpose/Purposemanagement" element={<PurposeManagement />}/>
                <Route path="/settings/dialyrate/Dailyratemanagement" element={<DailyRateManagement />} />
                <Route path="/settings/department/Departmentmanagement" element={<DepartmentManagement />}/>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </div>
          <Joyride
            key={`joyride-${currentStep}-${lastStepPrepared}`}
            continuous
            run={isRunning && isStepReady}
            steps={currentSteps}
            stepIndex={currentStep}
            callback={handleJoyrideCallback}
            showProgress
            showSkipButton
            disableOverlayClose
            disableCloseOnEsc
            spotlightClicks
            spotlightPadding={10}
            debug={true}
            styles={{
              options: {
                primaryColor: "#5cb7b7",
                zIndex: 1000, // Reduced to avoid overlap with sidebar
                overlayColor: "rgba(0, 0, 0, 0.8)",
              },
              tooltip: {
                fontSize: "18px",
                padding: "20px",
                backgroundColor: "#ffffff",
                border: "3px solid #5cb7b7",
                borderRadius: "10px",
                color: "#000",
                minWidth: "300px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                position: "absolute",
                zIndex: 1000,
              },
              tooltipContainer: {
                textAlign: "left",
              },
              buttonNext: {
                backgroundColor: "#5cb7b7",
                color: "#fff",
              },
              buttonBack: {
                color: "#5cb7b7",
              },
              buttonSkip: {
                color: "#ff0000",
              },
            }}
            floaterProps={{
              disableAnimation: false,
              styles: {
                floater: {
                  zIndex: 1000,
                },
                arrow: {
                  spread: 20,
                  length: 10,
                },
              },
            }}
          />
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppContent />
      </Router>
    </Provider>
  );
}

export default App;
